import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { EASE } from "@/lib/scroll";

const STEP_MS = 3800;

/** Reel-style how-to: plays the brand's real video when `video` is set, otherwise steps through the genuine shoot. */
export const HowToStory = ({ steps, video, title = "How to use it" }) => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0);
  const wrap = useRef(null);

  useEffect(() => {
    if (paused || video) return undefined;
    const id = setTimeout(() => {
      setI((v) => (v + 1) % steps.length);
      setTick((t) => t + 1);
    }, STEP_MS);
    return () => clearTimeout(id);
  }, [i, paused, steps.length, tick, video]);

  const go = (n) => {
    setI(((n % steps.length) + steps.length) % steps.length);
    setTick((t) => t + 1);
  };
  const onTap = (e) => {
    const r = wrap.current.getBoundingClientRect();
    go(e.clientX - r.left < r.width / 2 ? i - 1 : i + 1);
  };
  const step = steps[i];

  return (
    <figure
      ref={wrap}
      data-testid="blender-howto"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      className="group relative aspect-[3/4] select-none overflow-hidden rounded-[2px] bg-card lg:aspect-auto lg:h-full lg:min-h-[520px]"
    >
      {video ? (
        <video src={video.src} poster={video.poster} muted loop playsInline autoPlay controls={false} className="absolute inset-0 h-full w-full object-cover" data-testid="blender-howto-video" />
      ) : (
        <AnimatePresence initial={false}>
          <motion.img
            key={step.id}
            src={step.img}
            alt={step.title}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/45 via-transparent to-black/80" />
      <button aria-label="Previous or next step" onClick={onTap} className="absolute inset-0 z-10 cursor-pointer" data-testid="blender-howto-tap" />

      <div className="pointer-events-none absolute inset-x-4 top-4 z-20 flex gap-1.5" data-testid="blender-howto-progress">
        {steps.map((s, k) => (
          <span key={s.id} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30">
            {k === i && !video ? (
              <motion.span key={tick} className="block h-full bg-white" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: STEP_MS / 1000, ease: "linear" }} />
            ) : (
              <span className="block h-full bg-white" style={{ width: k < i || video ? "100%" : "0%" }} />
            )}
          </span>
        ))}
      </div>
      <div className="pointer-events-none absolute left-4 top-8 z-20 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white/90">
        {paused ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />} {title}
      </div>

      <figcaption className="pointer-events-none absolute inset-x-4 bottom-4 z-20 sm:inset-x-6 sm:bottom-6">
        <AnimatePresence mode="wait">
          <motion.div key={step.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.45, ease: EASE }}>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#e6c594]">Step 0{i + 1} / 0{steps.length}</p>
            <p data-testid="blender-howto-step" className="mt-1 font-display text-3xl leading-tight text-white sm:text-4xl">{step.title}</p>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-white/80">{step.note}</p>
          </motion.div>
        </AnimatePresence>
      </figcaption>
    </figure>
  );
};
